/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import Select from "react-select";
import {
  airportUse,
  colorOptions,
  distanceOptions,
  fuelOptions,
  groundTransportationOptions,
  hangarOptions,
  lighting,
  patternOptions,
} from "../data";
import { MdQuestionMark } from "react-icons/md";
import Swal from "sweetalert2";

const AirportInformationFields = () => {
  const {
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext(); // Access form context

  const watchFieldArray = watch("airports");

  const { fields, append, remove } = useFieldArray({
    control, // Pass the control object from `useForm`
    name: "airports", // Name of the field array
  });

  // Combine field metadata and watched values for each dependant
  const airPortList = fields.map((field, index) => {
    return {
      ...field, // Include field metadata from `useFieldArray`
      ...watchFieldArray?.[index], // Include watched values from `watch`
    };
  });

  // Use refs to avoid querying the DOM
  const feetYRef = useRef(null);
  const metersXRef = useRef(null);
  const metersYRef = useRef(null);
  const elevationFeetRef = useRef(null);

  // Function to handle input
  const handleInputChange = (event, onChange) => {
    const { value } = event.target;
    onChange(value); // Update the input value
  };

  return (
    <div className="mt-4">
      <h4 className="!mb-2 text-2xl font-bold flex items-center">
        Airport Information
        <MdQuestionMark
          onClick={() =>
            Swal.fire({
              text: `All of this information can be found on websites like AirNav.com or apps like ForeFlight.........


Please fill in all of the required fields below for the airport / heliport / seaplane base closest to your property that you want your guests to use.........


If there is any additional information beyond these fields, kindly put that into the description box above.........


If you have more than one airport that you would like to list, click the add airport button at the bottom of this section.`,
              icon: "question",
            })
          }
          style={{
            color: "#fff",
            background: "#8ec639",
            borderRadius: "50%",
            fontWeight: "bold",
            fontSize: "1.2rem",
            marginLeft: "8px",
            boxShadow: "0 0 8px 2px #8ec639, 0 0 0 3px #c6f6d5",
            border: "2px solid #8ec639",
            cursor: "pointer",
            padding: "2px",
            animation: "pulse-glow 1.2s infinite alternate",
          }}
          className="focus:outline-none focus:ring-2 focus:ring-[#8ec639]"
          title="Info."
        />
      </h4>
      <small className="mb-4 block text-sm text-gray-500">
        {`Note: If you have any additional information about the airport, please include it in the description above.`}
      </small>

      <div className="col-span-2 mt-4">
        <label
          htmlFor="helicopter_allowed"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          Helicopters Allowed
        </label>
        <Controller
          name={`helicopter_allowed`}
          control={control}
          render={({ field: { onChange, value } }) => (
            <select
              id="helicopter_allowed"
              className={`peer w-full rounded-md border p-3  ${
                errors?.helicopter_allowed?.message
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              value={+value}
              onChange={onChange}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          )}
        />
        {errors?.helicopter_allowed?.message && (
          <p className="mt-1 text-sm text-red-500">
            {errors?.helicopter_allowed?.message}
          </p>
        )}
      </div>

      {airPortList?.map((airPort, index) => {
        return (
          <div key={airPort.id} id="airports">
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].airport_identifier`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Identifier
                </label>
                <Controller
                  name={`airports[${index}].airport_identifier`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <div className="relative">
                      <input
                        id={`airports[${index}].airport_identifier`}
                        type="text"
                        className={`w-full rounded-md border px-4 py-3  ${
                          errors.airports?.[index]?.airport_identifier?.message
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="e.g., EGHG"
                        value={value}
                        onChange={onChange}
                      />
                      {(errors.airports?.[index]?.airport_identifier?.message ||
                        "Please enter the 2- to 4-digit identifier OR specify if it’s  uncharted, a water landing, etc.") && (
                        <p className="mt-1 text-sm text-gray-500">
                          {errors.airports?.[index]?.airport_identifier?.message
                            ? errors.airports?.[index]?.airport_identifier
                                ?.message
                            : "Please enter the 2- to 4-digit identifier OR specify if it’s  uncharted, a water landing, etc."}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].airport_name`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Airport Name
                </label>
                <Controller
                  name={`airports[${index}].airport_name`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <input
                      id={`airports[${index}].airport_name`}
                      type="text"
                      className={`w-full rounded-md border px-4 py-3  ${
                        errors.airports?.[index]?.airport_name?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="e.g., Heathrow Airport"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                {errors.airports?.[index]?.airport_name?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.airport_name?.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].airport_use`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Airport Use
                </label>
                <Controller
                  name={`airports[${index}].airport_use`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <select
                      id={`airports[${index}].airport_use`}
                      className={`w-full rounded-md border p-3  ${
                        errors.airports?.[index]?.airport_use?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={value}
                      onChange={onChange}
                    >
                      <option value="">Select a use</option>
                      {airportUse.map((option) => (
                        <option key={option.key} value={option.value}>
                          {option.value}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.airports?.[index]?.airport_use?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.airport_use?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].operation_hours`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Operation Hours
                </label>
                <Controller
                  name={`airports[${index}].operation_hours`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <input
                      id={`airports[${index}].operation_hours`}
                      type="text"
                      className={`w-full rounded-md border px-4 py-3  ${
                        errors.airports?.[index]?.operation_hours?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="e.g., 24 hours"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                {errors.airports?.[index]?.operation_hours?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.operation_hours?.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].lighting`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Lighting
                </label>
                <Controller
                  name={`airports[${index}].lighting`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <select
                      id={`airports[${index}].lighting`}
                      className={`w-full rounded-md border p-3  ${
                        errors.airports?.[index]?.lighting?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={value}
                      onChange={onChange}
                    >
                      <option value="">Select an option</option>
                      {lighting.map((option) => (
                        <option key={option.key} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.airports?.[index]?.lighting?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.lighting?.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].ctaf_unicom`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  CTAF/UNICOM
                </label>
                <Controller
                  name={`airports[${index}].ctaf_unicom`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <input
                      id={`airports[${index}].ctaf_unicom`}
                      type="text"
                      className={`w-full rounded-md border px-4 py-3  ${
                        errors.airports?.[index]?.ctaf_unicom?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="e.g., 122.8"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                {errors.airports?.[index]?.ctaf_unicom?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.ctaf_unicom?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].fuel`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Fuel
                </label>
                <Controller
                  name={`airports[${index}].fuel`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      id={`airports[${index}].fuel`}
                      isMulti
                      options={fuelOptions}
                      placeholder="Select fuel options..."
                      //   styles={customStyles}
                      value={fuelOptions.filter((option) =>
                        value.includes(option.value)
                      )}
                      onChange={(selectedOptions) => {
                        onChange(
                          selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : []
                        );
                      }}
                    />
                  )}
                />
                {errors.airports?.[index]?.fuel?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.fuel?.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].parking`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Parking
                </label>
                <Controller
                  name={`airports[${index}].parking`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <select
                      id={`airports[${index}].parking`}
                      className={`w-full rounded-md border p-3  ${
                        errors.airports?.[index]?.parking?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={value}
                      onChange={onChange}
                    >
                      <option value="">Select an option</option>
                      {hangarOptions.map((option) => (
                        <option key={option.key} value={option.value}>
                          {option.value}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.airports?.[index]?.parking?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.parking?.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].orientation`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Orientation
                </label>
                <Controller
                  name={`airports[${index}].orientation`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <input
                      id={`airports[${index}].orientation`}
                      type="text"
                      className={`w-full rounded-md border px-4 py-3  ${
                        errors.airports?.[index]?.orientation?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="XX/XX"
                      value={value}
                      onChange={(e) => {
                        let { value: val } = e.target;
                        val = val.replace(/[^\d]/g, "");
                        if (val.length > 2) {
                          val = `${val.slice(0, 2)}/${val.slice(2)}`;
                        }
                        if (val.length > 5) {
                          val = val.slice(0, 5);
                        }
                        onChange(val);
                      }}
                    />
                  )}
                />
                {errors.airports?.[index]?.orientation?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.orientation?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 items-start gap-4 md:grid-cols-3">
              <div className="col-span-1">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Dimensions
                </label>
                <div className="flex w-full overflow-hidden rounded-md !border !border-solid !border-gray-300">
                  <Controller
                    name={`airports[${index}].dimension_feetX`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="relative flex-grow">
                        <input
                          id={`airports[${index}].dimension_feetX`}
                          value={value}
                          onChange={(e) => handleInputChange(e, onChange)}
                          type="number"
                          className="h-12 w-full border-r border-gray-300 pl-4 pr-6 text-base focus:outline-none"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 transform text-gray-500">
                          X
                        </span>
                      </div>
                    )}
                  />
                  <Controller
                    name={`airports[${index}].dimension_feetY`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="relative flex-grow">
                        <input
                          id={`airports[${index}].dimension_feetY`}
                          ref={feetYRef}
                          type="number"
                          value={value}
                          onChange={(e) => {
                            handleInputChange(e, onChange);
                            setValue(
                              `airports[${index}].dimension_feets`,
                              `${getValues(
                                `airports[${index}].dimension_feetX`
                              )} X ${e.target.value}`
                            );
                          }}
                          className="h-12 w-full border-r border-gray-300 pl-4 pr-6 text-base focus:outline-none"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 transform text-gray-500">
                          ft./
                        </span>
                      </div>
                    )}
                  />
                  <Controller
                    name={`airports[${index}].dimension_metersX`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="relative flex-grow">
                        <input
                          id={`airports[${index}].dimension_metersX`}
                          ref={metersXRef}
                          type="number"
                          value={value}
                          onChange={(e) => handleInputChange(e, onChange)}
                          className="h-12 w-full border-r border-gray-300 pl-4 pr-6 text-base focus:outline-none"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 transform text-gray-500">
                          X
                        </span>
                      </div>
                    )}
                  />
                  <Controller
                    name={`airports[${index}].dimension_metersY`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="relative flex-grow">
                        <input
                          id={`airports[${index}].dimension_metersY`}
                          ref={metersYRef}
                          type="number"
                          value={value}
                          onChange={(e) => {
                            handleInputChange(e, onChange);
                            setValue(
                              `airports[${index}].dimension_meters`,
                              `${getValues(
                                `airports[${index}].dimension_metersX`
                              )} X ${e.target.value}`
                            );
                          }}
                          className="h-12 w-full rounded-r-md pl-4 pr-6 text-base focus:outline-none"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 transform text-gray-500">
                          m
                        </span>
                      </div>
                    )}
                  />
                </div>
                {(errors?.airports?.[index]?.dimension_feetX?.message ||
                  errors?.airports?.[index]?.dimension_feetY?.message ||
                  errors?.airports?.[index]?.dimension_metersX?.message ||
                  errors?.airports?.[index]?.dimension_metersY?.message) && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors?.airports?.[index]?.dimension_feetX?.message ||
                      errors?.airports?.[index]?.dimension_feetY?.message ||
                      errors?.airports?.[index]?.dimension_metersX?.message ||
                      errors?.airports?.[index]?.dimension_metersY?.message}
                  </p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].surface`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Surface
                </label>
                <Controller
                  name={`airports[${index}].surface`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Select
                        id={`airports[${index}].surface`}
                        isMulti
                        options={colorOptions}
                        placeholder="Select surface options..."
                        //   styles={customStyles}
                        value={colorOptions.filter((option) =>
                          value.includes(option.value)
                        )}
                        onChange={(selectedOptions) => {
                          onChange(
                            selectedOptions
                              ? selectedOptions.map((option) => option.value)
                              : []
                          );
                        }}
                      />
                    </>
                  )}
                />
                {errors.airports?.[index]?.surface?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.surface?.message}
                  </p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].runway_condition`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Condition of Runway
                </label>
                <Controller
                  name={`airports[${index}].runway_condition`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <input
                      id={`airports[${index}].runway_condition`}
                      type="text"
                      className={`w-full rounded-md border px-4 py-3  ${
                        errors.airports?.[index]?.runway_condition?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="e.g., Good"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                {errors.airports?.[index]?.runway_condition?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.runway_condition?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].pattern`}
                  className="!mb-2 block text-sm font-medium text-gray-700"
                >
                  Pattern
                </label>
                <Controller
                  name={`airports[${index}].pattern`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <select
                      id={`airports[${index}].pattern`}
                      className={`w-full rounded-md border p-3  ${
                        errors.airports?.[index]?.pattern?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={value}
                      onChange={onChange}
                    >
                      <option value="">Select a pattern</option>
                      {patternOptions.map((option) => (
                        <option key={option.key} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.airports?.[index]?.pattern?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.pattern?.message}
                  </p>
                )}
              </div>

              <div className="col-span-1">
                <label className="mb-2 flex items-center text-sm font-medium text-gray-700">
                  Elevation
                  <MdQuestionMark
                    onClick={() =>
                      Swal.fire({
                        text: `Elevation at each end of runway.
This helps our pilots know if the runway has a slope.
Please round to the nearest whole number.`,
                        icon: "question",
                      })
                    }
                    style={{
                      color: "#fff",
                      background: "#8ec639",
                      borderRadius: "50%",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                      marginLeft: "8px",
                      boxShadow: "0 0 8px 2px #8ec639, 0 0 0 3px #c6f6d5",
                      border: "2px solid #8ec639",
                      cursor: "pointer",
                      padding: "2px",
                      animation: "pulse-glow 1.2s infinite alternate",
                    }}
                    className="focus:outline-none focus:ring-2 focus:ring-[#8ec639]"
                    title="Info."
                  />
                </label>
                <div className="flex w-full overflow-hidden rounded-md border border-solid border-gray-300">
                  <Controller
                    name={`airports[${index}].elevation_feets`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="relative flex-grow">
                        <input
                          id={`airports[${index}].elevation_feets`}
                          value={value}
                          onChange={(e) => handleInputChange(e, onChange)}
                          type="number"
                          className="h-12 w-full border-r border-gray-300 pl-4 pr-6 text-base focus:outline-none"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 transform text-gray-500">
                          ft./
                        </span>
                      </div>
                    )}
                  />
                  <Controller
                    name={`airports[${index}].elevation_meters`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="relative flex-grow">
                        <input
                          id={`airports[${index}].elevation_meters`}
                          ref={elevationFeetRef}
                          type="number"
                          value={value}
                          onChange={(e) => handleInputChange(e, onChange)}
                          className="h-12 w-full rounded-r-md pl-4 pr-6 text-base focus:outline-none"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 transform text-gray-500">
                          m
                        </span>
                      </div>
                    )}
                  />
                </div>
                {(errors?.airports?.[index]?.elevation_feets?.message ||
                  errors?.airports?.[index]?.elevation_meters?.message) && (
                  <p className="mt-1 text-start text-sm text-red-500">
                    {errors?.airports?.[index]?.elevation_feets?.message ||
                      errors?.airports?.[index]?.elevation_meters?.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].distance_from_runway`}
                  className="!mb-2 flex items-center text-sm font-medium text-gray-700"
                >
                  Distance From Runway to Accommodations
                  <MdQuestionMark
                    onClick={() =>
                      Swal.fire({
                        text: `Zero means the runway is on the property (as in a private runway or an airpark)…
0.1 to 1 mile means the property is across the street from the airport, or up to a mile away.`,
                        icon: "question",
                      })
                    }
                    style={{
                      color: "#fff",
                      background: "#8ec639",
                      borderRadius: "50%",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                      marginLeft: "8px",
                      boxShadow: "0 0 8px 2px #8ec639, 0 0 0 3px #c6f6d5",
                      border: "2px solid #8ec639",
                      cursor: "pointer",
                      padding: "2px",
                      animation: "pulse-glow 1.2s infinite alternate",
                    }}
                    className="focus:outline-none focus:ring-2 focus:ring-[#8ec639]"
                    title="Info."
                  />
                </label>
                <Controller
                  name={`airports[${index}].distance_from_runway`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <select
                      id={`airports[${index}].distance_from_runway`}
                      className={`w-full rounded-md border p-3  ${
                        errors.airports?.[index]?.distance_from_runway?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={value}
                      onChange={onChange}
                    >
                      <option value="">Select a distance</option>
                      {distanceOptions.map((option) => (
                        <option key={option.key} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.airports?.[index]?.distance_from_runway?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.distance_from_runway?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].air_nav`}
                  className="mb-1 flex items-center text-sm font-medium text-gray-700"
                >
                  Airnav.com Information
                  <MdQuestionMark
                    onClick={() =>
                      Swal.fire({
                        text: `Please find your airport on airnav.com and enter the URL here so people can look it up for additional information.`,
                        icon: "question",
                      })
                    }
                    style={{
                      color: "#fff",
                      background: "#8ec639",
                      borderRadius: "50%",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                      marginLeft: "8px",
                      boxShadow: "0 0 8px 2px #8ec639, 0 0 0 3px #c6f6d5",
                      border: "2px solid #8ec639",
                      cursor: "pointer",
                      padding: "2px",
                      animation: "pulse-glow 1.2s infinite alternate",
                    }}
                    className="focus:outline-none focus:ring-2 focus:ring-[#8ec639]"
                    title="Info."
                  />
                </label>
                <Controller
                  name={`airports[${index}].air_nav`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <input
                      id={`airports[${index}].air_nav`}
                      type="text"
                      className={`w-full rounded-md border px-4 py-3  ${
                        errors.airports?.[index]?.air_nav?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="https://airnav.com/airport/"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                {errors.airports?.[index]?.air_nav?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.air_nav?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].ground_transportation`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Ground Transportation
                </label>
                <Controller
                  name={`airports[${index}].ground_transportation`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <select
                      id={`airports[${index}].ground_transportation`}
                      className={`w-full rounded-md border p-3  ${
                        errors.airports?.[index]?.ground_transportation?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={value}
                      onChange={onChange}
                    >
                      <option value="">Select a ground transportation</option>
                      {groundTransportationOptions.map((option) => (
                        <option key={option.key} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />

                {errors.airports?.[index]?.ground_transportation?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.ground_transportation?.message}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor={`airports[${index}].additional_info`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Additional Ground Transportation Info
                </label>
                <Controller
                  name={`airports[${index}].additional_info`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <input
                      id={`airports[${index}].additional_info`}
                      type="text"
                      className={`w-full rounded-md border px-4 py-3  ${
                        errors.airports?.[index]?.additional_info?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="e.g., Taxi, Uber, Shuttle"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                {errors.airports?.[index]?.additional_info?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.airports?.[index]?.additional_info?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1">
              {airPortList?.length > 1 && (
                <div className="col-span-1 flex justify-end">
                  <button
                    type="button"
                    className="rounded-md border border-red-500 bg-white px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    onClick={() => remove(index)}
                  >
                    Remove airport
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="mt-2 rounded-md border border-solid border-red-500 bg-white px-4 py-2 text-sm font-medium text-red-500 shadow-sm hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        onClick={() => {
          append({
            airport_identifier: "",
            airport_name: "",
            airport_use: "",
            operation_hours: "",
            lighting: 0,
            ctaf_unicom: "",
            fuel: [],
            surface: [],
            parking: "",
            orientation: "",
            runway_condition: "",
            pattern: "",
            elevation_feets: "",
            elevation_meters: "",
            distance_from_runway: "",
            air_nav: "",
            ground_transportation: "",
            helicopter_allowed: 0,
            additional_info: "",
            dimension_feetX: "",
            dimension_feetY: "",
            dimension_metersX: "",
            dimension_metersY: "",
          });
        }}
      >
        Add new airport
      </button>
    </div>
  );
};

export default AirportInformationFields;
